import React, { memo, useCallback, useContext, useMemo } from 'react';
import ScheduleFormWidget from '../../common/components/schedule/ScheduleFormWidget';
import { ScheduleFormWidgetValue, SupportedSchedulerDurationUnit } from '../../common/components/schedule/types';
import { FormContext } from '../../common/form/FormContext';
import { recalculateProductionYears } from '../../supply/booking/ScheduleCalculator';
import type { ProductionInputWithOwnerOrganization } from '../helpers/productionUtils';

const PRODUCTION_SCHEDULE_ALLOWED_DURATION_UNITS: SupportedSchedulerDurationUnit[] = ['day', 'week', 'month'];
const DEFAULT_PRODUCTION_DURATION_UNIT: SupportedSchedulerDurationUnit = 'week';
type ProductionDateRange = NonNullable<ProductionInputWithOwnerOrganization['productionDates']>['dateRange'];

const toScheduleFormWidgetValue = (dateRange: ProductionDateRange | undefined): ScheduleFormWidgetValue => {
    return {
        durationSpecified: Boolean(dateRange?.durationSpecified),
        startDate: dateRange?.start || '',
        startTime: dateRange?.startTime || '',
        endDate: dateRange?.end || '',
        endTime: dateRange?.endTime || '',
        duration: {
            unit:
                (dateRange?.dateTimeDuration?.unit as SupportedSchedulerDurationUnit) ||
                DEFAULT_PRODUCTION_DURATION_UNIT,
            value: dateRange?.dateTimeDuration?.value
        }
    };
};

function ProductionScheduleFormWidgetNonMemo() {
    const { setState, state } = useContext(FormContext);
    const dateRange = state?.productionDates?.dateRange;
    const scheduleValue = useMemo(() => toScheduleFormWidgetValue(dateRange), [dateRange]);

    const handleScheduleChange = useCallback(
        (nextValue: ScheduleFormWidgetValue) => {
            setState((prevState: ProductionInputWithOwnerOrganization) => {
                const nextState: ProductionInputWithOwnerOrganization = {
                    ...prevState,
                    productionDates: {
                        ...prevState.productionDates,
                        dateRange: {
                            ...prevState.productionDates?.dateRange,
                            start: nextValue.startDate,
                            startTime: nextValue.startTime || '',
                            end: nextValue.endDate || '',
                            endTime: nextValue.endTime || '',
                            durationSpecified: nextValue.durationSpecified,
                            dateTimeDuration: {
                                ...prevState.productionDates?.dateRange?.dateTimeDuration,
                                unit: nextValue.duration.unit,
                                value: nextValue.duration.value
                            }
                        }
                    }
                };

                recalculateProductionYears('productionDates.dateRange', nextValue, nextState);
                return nextState;
            });
        },
        [setState]
    );

    return (
        <ScheduleFormWidget
            id="production-schedule"
            variant="standard-date-only"
            value={scheduleValue}
            onChange={handleScheduleChange}
            allowedDurationUnits={PRODUCTION_SCHEDULE_ALLOWED_DURATION_UNITS}
            specifyDurationFormFieldLabelToken="production.activityForm.specifyDuration"
        />
    );
}

const ProductionScheduleFormWidget = memo(ProductionScheduleFormWidgetNonMemo);
export default ProductionScheduleFormWidget;
